import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Phone, Crown, Lock, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';
import { parseLocalDate, formatYMD } from '../utils/dateHelpers';

/**
 * MyAvailabilityPanel — tradesperson dashboard "My Availability" section.
 *
 * Reuses the same calendar layout employers see, but adds click-to-block /
 * click-to-unblock for non-booked days. Booked days are read-only.
 */

const STATUS_STYLES = {
  available:    { bg: 'bg-emerald-500',  ring: 'ring-emerald-600',  label: 'Available' },
  evening_only: { bg: 'bg-indigo-400',   ring: 'ring-indigo-500',   label: 'Evenings' },
  weekend_only: { bg: 'bg-amber-400',    ring: 'ring-amber-500',    label: 'Weekends' },
  booked:       { bg: 'bg-rose-500',     ring: 'ring-rose-600',     label: 'Booked' },
  unavailable:  { bg: 'bg-slate-400',    ring: 'ring-slate-500',    label: 'Unavailable' },
  unknown:      { bg: 'bg-slate-100',    ring: 'ring-slate-200',    label: 'Not set' },
};

function formatNiceDate(iso) {
  return formatYMD(iso, { weekday: 'short', day: 'numeric', month: 'short' });
}

export default function MyAvailabilityPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyDate, setBusyDate] = useState(null);

  const fetchAvailability = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/tradespeople/me/availability`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error('Failed to load availability:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAvailability(); }, [fetchAvailability]);

  const handleDayClick = async (cell) => {
    if (!cell || busyDate) return;
    if (cell.is_booked) {
      toast.info('This day has a booking — manage bookings from your jobs list.');
      return;
    }
    setBusyDate(cell.date);
    try {
      if (cell.is_user_blocked) {
        await axios.delete(`${BACKEND_URL}/api/tradespeople/me/blocked-dates/${cell.date}`, { withCredentials: true });
        toast.success(`${formatNiceDate(cell.date)} unblocked`);
      } else {
        await axios.post(`${BACKEND_URL}/api/tradespeople/me/blocked-dates`, { date: cell.date }, { withCredentials: true });
        toast.success(`${formatNiceDate(cell.date)} blocked`);
      }
      await fetchAvailability();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update that date');
    } finally {
      setBusyDate(null);
    }
  };

  if (loading) {
    return (
      <div className="card mb-6" data-testid="my-availability-loading">
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading availability…</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="card mb-6 text-slate-500 text-sm" data-testid="my-availability-empty">
        Set up your profile to manage availability.
      </div>
    );
  }

  // Build Mon-first weeks
  const days = data.calendar || [];
  const firstDate = days.length ? (parseLocalDate(days[0].date) || new Date()) : new Date();
  const leading = (firstDate.getDay() + 6) % 7;
  const padded = [...Array(leading).fill(null), ...days];
  while (padded.length % 7 !== 0) padded.push(null);
  const weeks = [];
  for (let i = 0; i < padded.length; i += 7) weeks.push(padded.slice(i, i + 7));

  return (
    <div className="card mb-6" data-testid="my-availability-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-orange-500" />
          My Availability
        </h3>
        <div className="flex items-center gap-2">
          {data.is_on_call && (
            <span className="inline-flex items-center gap-1 bg-emerald-100 border border-emerald-300 text-emerald-800 px-2.5 py-1 rounded-full text-xs font-bold" data-testid="my-availability-on-call-badge">
              <Phone className="h-3 w-3" /> On Call
            </span>
          )}
          {data.is_priority_boosted && (
            <span className="inline-flex items-center gap-1 bg-orange-100 border border-orange-300 text-orange-800 px-2.5 py-1 rounded-full text-xs font-bold" data-testid="my-availability-priority-badge">
              <Crown className="h-3 w-3" /> Priority
            </span>
          )}
        </div>
      </div>

      <p className="text-sm text-slate-500 mb-4" data-testid="my-availability-helper">
        Keep this updated so employers know when you're free. Click any day to block or unblock it.
      </p>

      {/* Calendar grid */}
      <div className="bg-slate-50 rounded-lg p-3 mb-3" data-testid="my-availability-grid">
        <div className="grid grid-cols-7 gap-1.5 mb-1">
          {['M','T','W','T','F','S','S'].map((d, i) => (
            <div key={`hdr-${i}`} className="text-center text-[10px] font-semibold text-slate-500 uppercase tracking-wide">{d}</div>
          ))}
        </div>
        <div className="space-y-1.5">
          {weeks.map((week, wi) => (
            <div key={`w-${wi}`} className="grid grid-cols-7 gap-1.5">
              {week.map((cell, ci) => {
                if (!cell) return <div key={`c-${wi}-${ci}`} className="aspect-square" />;
                const style = STATUS_STYLES[cell.status] || STATUS_STYLES.unknown;
                const dateNum = (parseLocalDate(cell.date) || new Date()).getDate();
                const isBooked = cell.is_booked;
                const isBlocked = cell.is_user_blocked;
                const interactive = !isBooked;
                const title = isBooked
                  ? `${formatNiceDate(cell.date)} — Booked (managed from jobs list)`
                  : isBlocked
                    ? `${formatNiceDate(cell.date)} — Blocked. Click to unblock.`
                    : `${formatNiceDate(cell.date)} — ${style.label}. Click to block.`;
                return (
                  <button
                    key={cell.date}
                    type="button"
                    title={title}
                    disabled={!interactive || busyDate === cell.date}
                    data-testid={`my-availability-day-${cell.date}`}
                    data-status={cell.status}
                    data-blocked={isBlocked ? 'true' : 'false'}
                    data-booked={isBooked ? 'true' : 'false'}
                    onClick={() => handleDayClick(cell)}
                    className={`relative aspect-square rounded-md ${style.bg} flex items-center justify-center text-[10px] font-bold text-white/95 ring-1 ${style.ring} ${interactive ? 'hover:ring-2 hover:ring-orange-400 cursor-pointer' : 'cursor-not-allowed opacity-90'} transition`}
                  >
                    {dateNum}
                    {isBooked && (
                      <Lock className="absolute -top-1 -right-1 h-3 w-3 text-rose-700 bg-white rounded-full p-0.5" />
                    )}
                    {busyDate === cell.date && (
                      <Loader2 className="absolute h-3.5 w-3.5 animate-spin" />
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-slate-600" data-testid="my-availability-legend">
        {Object.entries(STATUS_STYLES).map(([k, s]) => (
          <span key={k} className="flex items-center gap-1.5">
            <span className={`inline-block w-3 h-3 rounded ${s.bg}`} />
            {s.label}
          </span>
        ))}
      </div>

      {data.blocked_dates?.length > 0 && (
        <p className="mt-3 text-xs text-slate-500" data-testid="my-availability-blocked-count">
          {data.blocked_dates.length} day{data.blocked_dates.length !== 1 ? 's' : ''} blocked.
        </p>
      )}
    </div>
  );
}
